import { unscannedRanges } from '@/core/detection/scan-plan';
import type { DetectionKind } from '@/core/detection/types';
import type { PrivacySettings } from '@/core/project/recipe';
import type { AssetId } from '@/core/project/sequence';
import { normalizeRanges, range, type TimeRange } from '@/core/time/ranges';
import type { Track } from '@/core/tracking/track';
import { mergeTracks } from '@/core/tracking/tracker';
import type { EngineError } from '@/engine/errors';
import type { Progress } from '@/engine/types';

/**
 * Faces and plates are scanned separately, one after the other: faces show up
 * without waiting for plates, and each kind can be started, cancelled or
 * cleared on its own.
 */
export type KindScanStatus = 'idle' | 'queued' | 'running' | 'done' | 'error';

export interface KindScan {
  readonly status: KindScanStatus;
  readonly progress: Progress | null;
  readonly error: EngineError | null;
  /** A scan of this kind finished at least once, so its results are real (even if empty). */
  readonly completed: boolean;
  /** The running scan only looks at parts brought back into the edit since the last one. */
  readonly partial?: boolean;
}

export const DETECTION_KINDS: readonly DetectionKind[] = ['face', 'plate'];

export const IDLE_SCAN: KindScan = { status: 'idle', progress: null, error: null, completed: false };

export const idleScans = (): Record<DetectionKind, KindScan> => ({ face: IDLE_SCAN, plate: IDLE_SCAN });

/** Status a kind falls back to when its new scan stops early: keep what the last one found. */
export const settledScan = (scan: KindScan): KindScan => ({ ...scan, status: scan.completed ? 'done' : 'idle', progress: null, partial: false });

/** Applies `update` to the scan of every kind. */
export const mapScans = (scans: Record<DetectionKind, KindScan>, update: (kind: DetectionKind, scan: KindScan) => KindScan): Record<DetectionKind, KindScan> =>
  Object.fromEntries(DETECTION_KINDS.map((kind) => [kind, update(kind, scans[kind])])) as Record<DetectionKind, KindScan>;

export const isScanBusy = (scan: KindScan): boolean => scan.status === 'running' || scan.status === 'queued';

/** Replaces the tracks of `kind` with a new scan's results; other kinds stay as they are. */
export const replaceKindTracks = (tracks: readonly Track[], kind: DetectionKind, found: readonly Track[]): Track[] => [
  ...tracks.filter((track) => track.kind !== kind),
  ...found.filter((track) => track.kind === kind),
];

/** A new scan's tracks of `kind` in one video in place of the earlier ones, with ids that carry the video. */
export function replaceKindScan(tracks: readonly Track[], kind: DetectionKind, found: readonly Track[], assetId: string): { readonly tracks: Track[]; readonly ids: ReadonlyMap<string, string> } {
  const own = found.filter((track) => track.kind === kind);
  const ids = new Map(own.map((track) => [track.id, assetTrackId(track.id, assetId)]));
  return { tracks: replaceKindTracks(tracks, kind, own.map((track) => ({ ...track, id: ids.get(track.id)! }))), ids };
}

/** Track ids carry the video they were found in, so the same person id in two videos never collides. */
export const assetTrackId = (trackId: string, assetId: string): string => `${trackId}@${assetId}`;

/**
 * Drops "show this one" choices that pointed at tracks of `kind` (in one video
 * when `assetId` is given): ids restart with every scan, so an old choice
 * would land on a different person. `reveal` lists new tracks that should start visible.
 */
export function resetKindReveals(privacy: PrivacySettings, kind: DetectionKind, reveal: readonly string[] = [], assetId?: string): PrivacySettings {
  const prefix = `${kind}-`;
  const stale = (id: string) => id.startsWith(prefix) && (assetId === undefined || id.endsWith(`@${assetId}`));
  return { ...privacy, revealedTrackIds: [...privacy.revealedTrackIds.filter((id) => !stale(id)), ...reveal] };
}

/** Kinds whose scan finished: what a saved recipe should scan for on the next video. */
export const scannedKinds = (scans: Readonly<Record<DetectionKind, KindScan>>): DetectionKind[] => DETECTION_KINDS.filter((kind) => scans[kind].completed);

/**
 * Per kind and video: the video's own seconds that scans of that kind looked
 * at. Only what the edit shows is scanned, so parts brought back later (an
 * undone cut, a longer clip) are known to be unscanned and get scanned then.
 */
export type Coverage = Readonly<Record<DetectionKind, Readonly<Record<AssetId, readonly TimeRange[]>>>>;

export const emptyCoverage = (): Coverage => ({ face: {}, plate: {} });

/** Records that `ranges` of a video were scanned for `kind`; `replace` forgets what earlier scans covered. */
export function addCoverage(coverage: Coverage, kind: DetectionKind, assetId: AssetId, ranges: readonly TimeRange[], replace = false): Coverage {
  const before = replace ? [] : (coverage[kind][assetId] ?? []);
  return { ...coverage, [kind]: { ...coverage[kind], [assetId]: normalizeRanges([...before, ...ranges]) } };
}

/** Coverage of projects saved before parts were tracked: every video listed was scanned whole. */
export function wholeCoverage(scanned: Readonly<Record<DetectionKind, readonly AssetId[]>>, durationOf: (assetId: AssetId) => number | undefined): Coverage {
  let coverage = emptyCoverage();
  for (const kind of DETECTION_KINDS) {
    for (const assetId of scanned[kind] ?? []) {
      const duration = durationOf(assetId);
      if (duration && duration > 0) coverage = addCoverage(coverage, kind, assetId, [range(0, duration)]);
    }
  }
  return coverage;
}

/** Videos listed in a coverage for each kind (what older saved projects called `scanned`). */
export const scannedAssets = (coverage: Coverage): Record<DetectionKind, AssetId[]> =>
  Object.fromEntries(DETECTION_KINDS.map((kind) => [kind, Object.keys(coverage[kind])])) as Record<DetectionKind, AssetId[]>;

/** Of `assetIds`, the videos that show parts in the edit (`wantedOf`) no scan of `kind` looked at. */
export const unscannedAssets = (coverage: Coverage, kind: DetectionKind, assetIds: readonly AssetId[], wantedOf: (assetId: AssetId) => readonly TimeRange[]): AssetId[] =>
  assetIds.filter((assetId) => unscannedRanges(wantedOf(assetId), coverage[kind][assetId] ?? []).length > 0);

const trackNumber = (id: string): number => Number(/^[a-z]+-(\d+)/.exec(id)?.[1] ?? 0);

/**
 * Adds tracks of `kind` that a scan of more parts of one video found: ones that
 * continue a known person join them, the rest get ids not used in that video yet.
 * `found` carries the scan's own ids; `ids` says what each became.
 */
export function extendKindTracks(tracks: readonly Track[], kind: DetectionKind, found: readonly Track[], assetId: AssetId): { readonly tracks: Track[]; readonly ids: ReadonlyMap<string, string> } {
  const own = tracks.filter((track) => track.kind === kind);
  let next = Math.max(0, ...own.map((track) => trackNumber(track.id))) + 1;
  const merged = mergeTracks(
    own,
    found.filter((track) => track.kind === kind),
    () => assetTrackId(`${kind}-${next++}`, assetId),
  );
  return { tracks: [...tracks.filter((track) => track.kind !== kind), ...merged.tracks], ids: merged.ids };
}
